import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { UserProfile } from '@/entities/user/types';
import apiClient from '@/shared/utils/axios';

interface ProfileState {
  profile: UserProfile | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: ProfileState = {
  profile: null,
  isLoading: false,
  error: null,
};

// Async thunks
export const updateUserProfile = createAsyncThunk(
  'profile/updateUserProfile',
  async (
    { uid, data }: { uid: string; data: Partial<UserProfile> },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.put<UserProfile>(
        `/api/users/${uid}/profile`,
        data
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update user profile');
    }
  }
);

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearProfileError: state => {
      state.error = null;
    },
    resetProfileState: () => initialState,
    setProfile: (state, action: PayloadAction<UserProfile | null>) => {
      state.profile = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // Update user profile
      .addCase(updateUserProfile.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload;
        state.error = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearProfileError, resetProfileState, setProfile } =
  profileSlice.actions;
export default profileSlice.reducer;
